import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, Input, Modal } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';

import {
  useCreateCustomTag,
  useUpdateCustomTag,
} from 'app/hooks/data/useCustomTags';
import type { CustomTag } from 'app/typings/customTags';
import { cleanCustomTagString, doesCustomTagExist } from 'app/utils/customTags';

import type { CustomTagForm } from '../CustomTags';

import './CustomTagModal.scss';

type Props = {
  customTags?: CustomTag[];
  closeModal: () => void;
  selectedTag?: CustomTagForm | null;
};

export const CustomTagModal = ({
  customTags,
  closeModal,
  selectedTag,
}: Props) => {
  const [inputValue, setInputValue] = useState(selectedTag?.name || '');
  const { mutateAsync: createTag } = useCreateCustomTag();
  const { mutateAsync: updateCustomTag } = useUpdateCustomTag();

  const existingTag = !!selectedTag;

  useEffect(() => {
    if (existingTag) {
      setInputValue(selectedTag.name);
    } else {
      setInputValue('');
    }
  }, [existingTag, selectedTag]);

  const tagAlreadyExists = useMemo(
    () =>
      doesCustomTagExist(customTags, inputValue, selectedTag?.id ?? undefined),
    [customTags, inputValue, selectedTag?.id]
  );

  const isDirty = existingTag
    ? cleanCustomTagString(inputValue) !==
      cleanCustomTagString(selectedTag.name)
    : inputValue.trim() !== '';

  const isSaveButtonDisabled = !isDirty || tagAlreadyExists;

  const onSave = async () => {
    if (selectedTag?.id) {
      await updateCustomTag({ id: selectedTag.id, name: inputValue });
    } else {
      await createTag(inputValue);
    }

    closeModal();
  };

  const title = existingTag ? 'Edit Custom Tag' : 'Create New Custom Tag';

  return (
    <Modal
      width={800}
      onOk={onSave}
      okText="Save"
      okButtonProps={{ disabled: isSaveButtonDisabled }}
      onCancel={closeModal}
      open
      title={title}
      centered
      className="new-custom-tag-modal"
    >
      <Form layout="vertical">
        <Form.Item
          label="Custom Tag Name"
          validateStatus={tagAlreadyExists ? 'error' : ''}
          help={tagAlreadyExists ? 'This name is already used' : null}
        >
          <Input
            maxLength={25}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Custom tag name"
          />
          {!tagAlreadyExists && (
            <p className="maxLengthText">25 characters max.</p>
          )}
        </Form.Item>
      </Form>

      <div
        className={classNames('container-modal', {
          'extra-spacing': tagAlreadyExists,
        })}
      >
        <div className="details-modal-container">
          <div className="icon-text-wrapper">
            <InfoCircleOutlined className="icon-modal" />
            <p className="title-modal">Important</p>
          </div>
          <p className="text">
            Custom tag names are now <strong>visible to users,</strong> so:
            <br />
            &nbsp;&nbsp;• Use <strong>short names</strong>
            <br />
            &nbsp;&nbsp;• Write them in the <strong>user’s language</strong> —
            they
            <strong> won’t be translated.</strong>
            <br />
            <br />
            <i>Good to know:</i> these tags will appear as search result titles
            and special filters.
          </p>
        </div>
      </div>
    </Modal>
  );
};
